import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../footer/Footer';
import Navbar from '../headerSection/navbar/Navbar';
import Testimonials from '../testimonials/Testimonials';

/**
 * CategoryOverviewPage Component
 * Verkaufende Landing Page für Kategorien wie /stellendatenbank, /jobanzeigen, etc.
 */
const CategoryOverviewPage = ({ category }) => {
  const navigate = useNavigate();

  const handleDemoClick = () => {
    navigate('/demo');
  };

  // Nimm erste Seite als Basis für Content
  const firstPage = category.pages[0];

  // Grammatik-Check: Ist der Titel Plural?
  const isPlural = category.title.endsWith('en') || category.title.endsWith('Daten');
  const verbBieten = isPlural ? 'bieten' : 'bietet';

  return (
    <>
      <Helmet>
        <title>{category.title} | 5 Mio Jobs | Jobdaten</title>
        <meta name="description" content={category.description} />
        <link rel="canonical" href={`https://jobdaten.com/${category.slug}`} />
        <meta property="og:title" content={`${category.title} | 5 Mio Jobs | Jobdaten`} />
        <meta property="og:description" content={category.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://jobdaten.com/${category.slug}`} />
      </Helmet>

      <div className="w-full min-h-screen bg-gradient-to-b from-white via-[#faf8ff] to-white">
        <Navbar />
        
        {/* Hero Section - VERKAUFEND */}
        <section className="w-full pt-32 pb-16">
          <div className="project-container px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-6">
                {category.title}
              </h1>
              <h2 className="text-2xl md:text-3xl text-gray-700 mb-8 font-medium">
                {category.description}
              </h2>
              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
                {firstPage.uniqueIntro}
              </p>
              
              <div className="flex flex-wrap gap-4 justify-center mb-12">
                <button 
                  onClick={handleDemoClick}
                  className="px-8 py-4 bg-[#B588FF] text-white rounded-lg font-medium hover:bg-[#a374ff] transition-all shadow-lg hover:shadow-xl"
                >
                  Kostenlose Demo anfordern
                </button>
              </div>

              <div className="inline-block px-6 py-3 bg-gradient-to-r from-[#B588FF]/10 to-[#8B5CF6]/10 rounded-full">
                <span className="text-lg font-semibold text-gray-700">
                  {firstPage.uniqueStat}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section - WAS KANN DIE SOFTWARE */}
        <section className="w-full py-16 bg-white">
          <div className="project-container px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              Das {verbBieten} Ihnen {isPlural ? '' : 'unsere '}{category.title}
            </h2>
            <p className="text-lg text-gray-500 text-center mb-12 max-w-2xl mx-auto">
              Leistungsstarke Features für Recruiting, B2B-Vertrieb und Marktanalyse
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {firstPage.uniqueBenefits.map((benefit, idx) => (
                <div key={idx} className="flex gap-4 p-6 bg-gradient-to-br from-white to-[#faf8ff] rounded-xl border border-gray-200">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-gradient-to-br from-[#B588FF] to-[#8B5CF6] rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold">✓</span>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Kunden Testimonials */}
        <Testimonials />

        {/* FAQ Section */}
        <section className="w-full py-16 bg-white">
          <div className="project-container px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              Häufige Fragen zu {category.title}
            </h2>
            <p className="text-lg text-gray-500 text-center mb-12 max-w-2xl mx-auto">
              Alles, was Sie wissen müssen
            </p>
            
            <div className="max-w-3xl mx-auto space-y-6">
              {firstPage.uniqueFAQs.map((faq, idx) => (
                <div key={idx} className="bg-gradient-to-br from-white to-[#faf8ff] p-6 rounded-xl border border-gray-200">
                  <h3 className="text-xl font-bold text-black mb-3">{faq.q}</h3>
                  <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full py-16 bg-gradient-to-br from-[#B588FF] to-[#8B5CF6]">
          <div className="project-container px-4">
            <div className="max-w-3xl mx-auto text-center text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Bereit für {isPlural ? '' : 'unsere '}{category.title}?
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Starten Sie jetzt mit unserer kostenlosen Demo und erleben Sie die Power von 5 Millionen Jobs
              </p>
              <button 
                onClick={handleDemoClick}
                className="px-10 py-4 bg-white text-[#B588FF] rounded-lg font-bold hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl text-lg"
              >
                Jetzt kostenlos Demo anfordern
              </button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default CategoryOverviewPage;
